import {useCallback, useMemo, useState} from 'react';
import {Button, spaceHelpers, Table} from '@flixbus/honeycomb-react';
import {Icon, IconEditSolid} from '@flixbus/honeycomb-icons-react';
import DriverCostsPanel from '../DriverCostsPanel/DriverCostsPanel';
import {
  DriverCostsPanelData,
  RegionalCostDriverCostPanelData
} from '../DriverCostsPanel/DriverCostsPanel.types';
import {DriverCostTableProps} from './DriverCostsTable.types';
import styles from './DriverCostsTable.module.scss';
import {getDriverCostTableRowConfig} from './DriverCostsTable.utils';

const DriverCostTable: React.FC<DriverCostTableProps> = ({
  country,
  year,
  config,
  driverCost,
  fallbackConfig,
  fallbackDriverCost,
  updateFn
}) => {
  const {half: spaceHalf} = spaceHelpers();

  const rows = useMemo(() => {
    return config.map(rowConfig => {
      const {title, icon, originalValue, fallbackValue} =
        getDriverCostTableRowConfig(
          rowConfig,
          driverCost,
          fallbackConfig,
          fallbackDriverCost
        );

      const titleCell = (
        <div className={styles.cellContainer}>
          <div className={styles.cellTop}>
            {title}
            {icon && <Icon InlineIcon={icon} extraClasses={spaceHalf.left} />}:
          </div>
          <div>
            {fallbackValue !== null && (
              <div className={styles.fallback}>
                Regional value = {fallbackValue}
              </div>
            )}
          </div>
        </div>
      );

      return [titleCell, originalValue];
    });
  }, [config, fallbackConfig, fallbackDriverCost, driverCost, spaceHalf.left]);

  const [panelData, setPanelData] = useState<DriverCostsPanelData | null>(null);

  const handleEditClick = useCallback(() => {
    const baseData = {
      accommodationCostPerNight: driverCost.accommodationCostPerNight,
      daytimeWeekdaysWorkingHourCost: driverCost.daytimeWeekdaysWorkingHourCost,
      daytimeWeekendsWorkingHourCost: driverCost.daytimeWeekendsWorkingHourCost,
      nighttimeWeekdaysWorkingHourCost:
        driverCost.nighttimeWeekdaysWorkingHourCost,
      nighttimeWeekendsWorkingHourCost:
        driverCost.nighttimeWeekendsWorkingHourCost,
      replacementCostPerHr: driverCost.replacementCostPerHr,
      replacementCostPerKm: driverCost.replacementCostPerKm
    };

    const isRegionalDriverCost = 'dailyAllowance' in driverCost;
    const panelData = isRegionalDriverCost
      ? ({
          ...baseData,
          dailyAllowance: driverCost.dailyAllowance,
          prefilledEmptyDistance: driverCost.prefilledEmptyDistance,
          prefilledEmptyHr: driverCost.prefilledEmptyHr
        } as RegionalCostDriverCostPanelData)
      : (baseData as RegionalCostDriverCostPanelData);

    setPanelData(panelData);
  }, [driverCost]);

  const handleClose = useCallback(() => {
    setPanelData(null);
  }, []);

  return (
    <>
      <div className={styles.title}>
        <h3 className={styles.leftSide}>
          Define driver cost for {country.name}
        </h3>
        <Button onClick={handleEditClick}>
          <Icon InlineIcon={IconEditSolid} aria-hidden="true" />
          Edit cost
        </Button>
      </div>

      <Table rows={rows} extraClasses="driver-cost-table" />

      <DriverCostsPanel
        country={country}
        year={year}
        panelData={panelData}
        config={config}
        updateFn={updateFn}
        onClose={handleClose}
      />
    </>
  );
};

export default DriverCostTable;
